(() => {
  'use strict';

  const rootUrl = () => window.Shopify?.routes?.root || '/';
  const route = (path) => `${rootUrl().replace(/\/?$/, '/')}${String(path || '').replace(/^\//, '')}`;
  const normalize = (value) => String(value || '').trim();
  const splitSystems = (value) => String(value || '').split(',').map((item) => item.trim()).filter(Boolean);

  const money = (cents, currency = 'MXN') => {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'es-MX', { style: 'currency', currency }).format((Number(cents) || 0) / 100);
    } catch (_) {
      return `$${((Number(cents) || 0) / 100).toFixed(2)}`;
    }
  };

  const showToast = (message) => {
    let toast = document.querySelector('[data-kiwo-toast]');
    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'kiwo-toast';
      toast.dataset.kiwoToast = '';
      toast.setAttribute('role', 'status');
      toast.setAttribute('aria-live', 'polite');
      document.body.appendChild(toast);
    }
    toast.textContent = message;
    toast.classList.add('is-visible');
    window.clearTimeout(showToast.timer);
    showToast.timer = window.setTimeout(() => toast.classList.remove('is-visible'), 2800);
  };

  class KiwoFinder {
    constructor(root) {
      this.root = root;
      this.form = root.querySelector('[data-kiwo-finder-form]');
      this.typeSelect = root.querySelector('[data-kiwo-type]');
      this.modelSelect = root.querySelector('[data-kiwo-model]');
      this.systemSelect = root.querySelector('[data-kiwo-system]');
      this.tabs = root.querySelector('[data-kiwo-tabs]');
      this.cards = [...root.querySelectorAll('[data-kiwo-model-card]')];
      const dataNode = root.querySelector('[data-kiwo-model-data]');
      let config = {};
      try { config = JSON.parse(dataNode?.textContent || '{}'); } catch (error) { console.error('Kiwo: configuración inválida.', error); }
      this.config = config;
      this.models = (Array.isArray(config.models) ? config.models : []).map((model) => ({
        ...model,
        type: normalize(model.type),
        model: normalize(model.model),
        value: normalize(model.value || model.model),
        systems: splitSystems(model.systems),
        url: normalize(model.url),
      }));
      this.params = config.filterParams || {};
      this.enableType = Boolean(config.enableTypeFilter);
      this.enableSystem = Boolean(config.enableSystemFilter);
      if (!this.form || !this.typeSelect || !this.modelSelect || !this.systemSelect) return;
      this.renderTypes();
      this.renderTabs();
      this.bind();
    }

    uniqueTypes() {
      return [...new Set(this.models.map((item) => item.type).filter(Boolean))].sort((a, b) => a.localeCompare(b, 'es', { sensitivity: 'base' }));
    }

    renderTypes() {
      const types = this.uniqueTypes();
      this.typeSelect.innerHTML = '<option value="">Selecciona un tipo</option>';
      types.forEach((type) => this.typeSelect.add(new Option(type, type)));
      if (types.length === 1) {
        this.typeSelect.value = types[0];
        this.renderModels(types[0]);
      }
    }

    renderModels(type) {
      this.modelSelect.innerHTML = '<option value="">Selecciona un modelo</option>';
      this.resetSystems();
      const models = this.models.filter((item) => item.type === type).sort((a, b) => a.model.localeCompare(b.model, 'es', { sensitivity: 'base' }));
      models.forEach((item) => {
        const option = new Option(item.model, item.value);
        option.dataset.modelName = item.model;
        this.modelSelect.add(option);
      });
      this.modelSelect.disabled = models.length === 0;
    }

    resetSystems() {
      this.systemSelect.innerHTML = `<option value="">${this.enableSystem ? 'Todos los sistemas' : 'Se activa al cargar el metacampo Sistema'}</option>`;
      this.systemSelect.disabled = true;
    }

    renderSystems(modelValue) {
      this.resetSystems();
      if (!this.enableSystem) return;
      const selected = this.models.find((item) => item.value === modelValue);
      const systems = [...new Set(selected?.systems || [])].sort((a, b) => a.localeCompare(b, 'es', { sensitivity: 'base' }));
      systems.forEach((system) => this.systemSelect.add(new Option(system, system)));
      this.systemSelect.disabled = systems.length === 0;
    }

    renderTabs() {
      if (!this.tabs || this.cards.length === 0) return;
      this.tabs.replaceChildren(this.createTab('Todos', 'all', true));
      this.uniqueTypes().forEach((type) => this.tabs.appendChild(this.createTab(type, type, false)));
    }

    createTab(label, value, pressed) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'kiwo-tab';
      button.textContent = label;
      button.dataset.kiwoTab = value;
      button.setAttribute('aria-pressed', String(pressed));
      return button;
    }

    filterCards(type) {
      this.cards.forEach((card) => { card.hidden = type !== 'all' && card.dataset.kiwoModelType !== type; });
      this.tabs?.querySelectorAll('[data-kiwo-tab]').forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.kiwoTab === type)));
    }

    bind() {
      this.typeSelect.addEventListener('change', () => this.renderModels(this.typeSelect.value));
      this.modelSelect.addEventListener('change', () => this.renderSystems(this.modelSelect.value));
      this.form.addEventListener('submit', (event) => {
        event.preventDefault();
        const type = normalize(this.typeSelect.value);
        const model = normalize(this.modelSelect.value);
        const system = normalize(this.systemSelect.value);
        if (!type || !model) { showToast('Selecciona el tipo de vehículo y el modelo.'); return; }
        const params = new URLSearchParams();
        params.set('view', 'kiwo');
        if (this.params.model) params.set(this.params.model, model);
        if (this.enableType && this.params.type) params.set(this.params.type, type);
        if (this.enableSystem && system && this.params.system) params.set(this.params.system, system);
        window.location.assign(`${this.form.action}?${params.toString()}`);
      });
      this.tabs?.addEventListener('click', (event) => {
        const button = event.target.closest('[data-kiwo-tab]');
        if (button) this.filterCards(button.dataset.kiwoTab);
      });
    }
  }

  class KiwoProductDialog {
    constructor(dialog) {
      this.dialog = dialog;
      this.content = dialog.querySelector('[data-kiwo-dialog-content]');
      this.lastTrigger = null;
      dialog.querySelector('[data-kiwo-dialog-close]')?.addEventListener('click', () => this.close());
      dialog.addEventListener('click', (event) => { if (event.target === dialog) this.close(); });
      dialog.addEventListener('close', () => this.lastTrigger?.focus());
      document.addEventListener('click', (event) => {
        const trigger = event.target.closest('[data-kiwo-product-open]');
        if (trigger) { event.preventDefault(); this.open(trigger.dataset.kiwoProductOpen, trigger); }
      });
    }

    open(templateId, trigger) {
      const template = document.getElementById(templateId);
      if (!template || !this.content) return;
      this.lastTrigger = trigger;
      this.content.replaceChildren(template.content.cloneNode(true));
      if (typeof this.dialog.showModal === 'function') this.dialog.showModal();
      else this.dialog.setAttribute('open', '');
      this.dialog.querySelector('[data-kiwo-dialog-close]')?.focus();
    }

    close() {
      if (typeof this.dialog.close === 'function' && this.dialog.open) this.dialog.close();
      else this.dialog.removeAttribute('open');
    }
  }

  class KiwoQuoteDrawer {
    constructor(drawer) {
      this.drawer = drawer;
      this.overlay = document.querySelector('[data-kiwo-quote-overlay]');
      this.itemsNode = drawer.querySelector('[data-kiwo-quote-items]');
      this.totalNode = drawer.querySelector('[data-kiwo-quote-total]');
      this.emptyNode = drawer.querySelector('[data-kiwo-quote-empty]');
      this.whatsappButton = drawer.querySelector('[data-kiwo-whatsapp]');
      this.checkoutButton = drawer.querySelector('[data-kiwo-checkout]');
      this.currency = drawer.dataset.currency || 'MXN';
      this.phone = String(drawer.dataset.whatsapp || '').replace(/\D/g, '');
      this.mode = drawer.dataset.mode || 'whatsapp';
      this.cart = null;
      this.lastOpener = null;
      this.bind();
      this.refresh(false);
    }

    bind() {
      document.addEventListener('click', (event) => {
        const open = event.target.closest('[data-kiwo-quote-open]');
        if (open) { event.preventDefault(); this.open(open); }
        const remove = event.target.closest('[data-kiwo-remove-item]');
        if (remove) { event.preventDefault(); this.remove(remove.dataset.kiwoRemoveItem); }
      });
      document.addEventListener('submit', (event) => {
        const form = event.target.closest('[data-kiwo-add-form]');
        if (!form) return;
        event.preventDefault();
        this.add(form);
      });
      this.overlay?.addEventListener('click', () => this.close());
      this.drawer.querySelector('[data-kiwo-quote-close]')?.addEventListener('click', () => this.close());
      this.whatsappButton?.addEventListener('click', () => this.sendWhatsApp());
      this.checkoutButton?.addEventListener('click', (event) => { if (!this.cart?.items?.length) event.preventDefault(); });
      document.addEventListener('keydown', (event) => { if (event.key === 'Escape' && document.body.classList.contains('kiwo-quote-open')) this.close(); });
      document.addEventListener('on:cart:add', () => this.refresh(false));
      document.addEventListener('on:line-item:change', () => this.refresh(false));
    }

    open(opener) {
      this.lastOpener = opener || document.activeElement;
      document.body.classList.add('kiwo-quote-open');
      this.drawer.setAttribute('aria-hidden', 'false');
      this.refresh(false);
      this.drawer.querySelector('[data-kiwo-quote-close]')?.focus();
    }

    close() {
      document.body.classList.remove('kiwo-quote-open');
      this.drawer.setAttribute('aria-hidden', 'true');
      this.lastOpener?.focus?.();
    }

    async request(path, options = {}) {
      const requestOptions = { credentials: 'same-origin', ...options };
      if (options.body) requestOptions.headers = { 'Content-Type': 'application/json', ...(options.headers || {}) };
      const response = await fetch(route(path), requestOptions);
      if (!response.ok) {
        let message = 'No se pudo actualizar la cotización.';
        try { const data = await response.json(); message = data.description || data.message || message; } catch (_) {}
        throw new Error(message);
      }
      return response.json();
    }

    propertiesFromForm(form) {
      const properties = {};
      new FormData(form).forEach((value, key) => {
        const match = key.match(/^properties\[(.+)]$/);
        if (match && value !== '') properties[match[1]] = value;
      });
      return properties;
    }

    async add(form) {
      const button = form.querySelector('[type="submit"]');
      const id = Number(form.elements.id?.value);
      const quantity = Math.max(1, Number(form.elements.quantity?.value || 1));
      if (!id) return;
      try {
        if (button) { button.disabled = true; button.setAttribute('aria-busy', 'true'); }
        await this.request('cart/add.js', { method: 'POST', body: JSON.stringify({ items: [{ id, quantity, properties: this.propertiesFromForm(form) }] }) });
        await this.refresh(true);
        document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', { bubbles: true }));
        const openDialog = document.querySelector('[data-kiwo-dialog][open]');
        if (openDialog?.close) openDialog.close();
        this.open(button);
        showToast('Producto agregado a la cotización.');
      } catch (error) { showToast(error.message); }
      finally { if (button) { button.disabled = false; button.removeAttribute('aria-busy'); } }
    }

    async remove(key) {
      if (!key) return;
      try {
        await this.request('cart/change.js', { method: 'POST', body: JSON.stringify({ id: key, quantity: 0 }) });
        await this.refresh(false);
        document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', { bubbles: true }));
      } catch (error) { showToast(error.message); }
    }

    async refresh(throwOnError = false) {
      try { this.cart = await this.request('cart.js'); this.render(); }
      catch (error) { if (throwOnError) throw error; console.error('Kiwo:', error); }
    }

    render() {
      if (!this.cart || !this.itemsNode || !this.totalNode || !this.emptyNode) return;
      const items = this.cart.items || [];
      this.itemsNode.innerHTML = items.map((item) => `
        <article class="kiwo-quote-item">
          ${item.image ? `<img src="${this.escape(item.image)}" alt="${this.escape(item.product_title)}" loading="lazy">` : '<div class="kiwo-quote-item-placeholder" aria-hidden="true"></div>'}
          <div><h3>${this.escape(item.product_title)}</h3>${item.variant_title && item.variant_title !== 'Default Title' ? `<div class="kiwo-muted">${this.escape(item.variant_title)}</div>` : ''}<div class="kiwo-muted">${this.escape(item.sku || 'Sin SKU')} · ${item.quantity} pza.</div><strong>${money(item.final_line_price, this.currency)}</strong></div>
          <button type="button" class="kiwo-quote-remove" data-kiwo-remove-item="${this.escape(item.key)}">Quitar</button>
        </article>`).join('');
      this.emptyNode.hidden = items.length > 0;
      this.totalNode.textContent = money(this.cart.total_price, this.currency);
      document.querySelectorAll('[data-kiwo-quote-count]').forEach((node) => { node.textContent = this.cart.item_count; });
      if (this.whatsappButton) { this.whatsappButton.hidden = this.mode === 'checkout'; this.whatsappButton.disabled = items.length === 0 || !this.phone; }
      if (this.checkoutButton) { this.checkoutButton.hidden = this.mode === 'whatsapp'; this.checkoutButton.setAttribute('aria-disabled', String(items.length === 0)); this.checkoutButton.tabIndex = items.length === 0 ? -1 : 0; }
    }

    sendWhatsApp() {
      if (!this.cart?.items?.length) return;
      if (!this.phone) { showToast('Configura el número de WhatsApp en el editor del tema.'); return; }
      const lines = ['Hola, solicito cotización de las siguientes refacciones Kiwo:', '', ...this.cart.items.map((item) => `• ${item.product_title} | SKU ${item.sku || 'N/D'} | Cantidad ${item.quantity}`), '', `Subtotal mostrado: ${money(this.cart.total_price, this.currency)}`, 'Favor de confirmar compatibilidad, existencia, precio final, envío y tiempo de entrega.'];
      window.open(`https://wa.me/${this.phone}?text=${encodeURIComponent(lines.join('\n'))}`, '_blank', 'noopener,noreferrer');
    }

    escape(value) {
      return String(value || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#039;');
    }
  }

  const init = (scope = document) => {
    scope.querySelectorAll('[data-kiwo-finder]').forEach((node) => { if (!node.dataset.kiwoInitialized) { node.dataset.kiwoInitialized = 'true'; new KiwoFinder(node); } });
    document.querySelectorAll('[data-kiwo-dialog]').forEach((dialog) => { if (!dialog.dataset.kiwoInitialized) { dialog.dataset.kiwoInitialized = 'true'; new KiwoProductDialog(dialog); } });
    document.querySelectorAll('[data-kiwo-quote-drawer]').forEach((drawer) => { if (!drawer.dataset.kiwoInitialized) { drawer.dataset.kiwoInitialized = 'true'; new KiwoQuoteDrawer(drawer); } });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init()); else init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
